import type { ReactNode } from "react";
import React, { useEffect, useMemo, useState } from "react";
import type { ColumnHeader } from "@/components/MyTable";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useDebouncedInput } from "@/hooks/useDebouncedInput";
import { useGetData } from "@/hooks/useGetData";
import { Search, X, Download } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import * as XLSX from "xlsx";
import { useGetSearchParams } from "@/hooks/useGetSearchParams";
import FilterDropdown from "@/components/FilterDropDowns";
import clsx from "clsx";
import AssignMultiSelect from "@/components/AssignMultiSelect";
import { isPermissionNavRole, useAuth } from "@/contexts/use-auth";
import { TableRowCardDates } from "./TableRowCardDates";
import { PatientV2CircularLoader } from "../shared/PatientV2CircularLoader";
import {
  patientV2CardFieldLabelClass,
  patientV2CardFieldValueClass,
  patientV2ExpandableCardListClass,
} from "../constants";

type MoreActions = {
  label?: string;
  icon?: React.ReactNode;
  onClick?: (data: any) => void;
  isShow: (data?: any) => boolean;
  isDisabled?: (data?: any) => boolean;
  render?: (data: any) => React.ReactNode;
  preventClose?: boolean;
};

type RenderRowCardContext = {
  row: any;
  viewRow: any;
  index: number;
  isSelected: boolean;
  toggleSelected: (checked: boolean) => void;
  visibleHeaders: ColumnHeader[];
  actions: MoreActions[];
  onPrimaryAction?: () => void;
};

type Props = {
  heading: string;
  url: string;
  params: any;
  apiDataKey: string;
  fetchCondition: boolean;
  isIncludeSearch: boolean;
  searchPlaceholder: string;
  columnHeaders: ColumnHeader[];
  data: any;
  viewData: any;
  setData: any;
  isActionColumn: boolean;
  filtersOptions: any[];
  handleAction: (data: any) => void;
  handleEditAction?: (data: any) => void;
  filters: any;
  setFilters: (data: any) => void;
  clickRow: (data: any) => void;
  isDownloadData?: boolean;
  downloadAccessModule?: string;
  moreActions?: MoreActions[];
  isSelectable?: boolean;
  selectActions?: any[];
  handleSearchActionsClicked?: (
    selected: any,
    key: any,
    value: any,
    cb: any,
  ) => void;
  donotFetch?: boolean;
  isIgnoreSearchParams?: boolean;
  clickCell?: any;
  defaultFilters?: Record<string, any>;
  isActionDisabled?: (row: any) => boolean;
  isActionsHidden?: (row: any) => boolean;
  onDownload?: (ctx?: {
    search?: string;
    visibleColumnIds: string[];
    columnOrder: string[];
    visibleColumns: Record<string, boolean>;
    sort?: {
      sortKey?: string;
      sortType?: string;
    } | null;
  }) => void | Promise<void>;
  headerExtraControls?: ReactNode;
  searchWrapperClassName?: string;
  onClearAllFilters?: () => void;
  universalLoadingSkeleton?: boolean;
  clientSearchRowFilter?: (row: any, searchLower: string) => boolean;
  renderRowCard?: (ctx: RenderRowCardContext) => ReactNode;
  cardContainerClassName?: string;
  pageSizeOptions?: number[];
};

function rowIdValue(row: any, fallbackIndex: number) {
  return row?._id ?? row?.id ?? `row-${fallbackIndex}`;
}

function buildRowSearchHaystack(
  row: any,
  viewRow: any,
  columnHeaders: ColumnHeader[],
): string {
  if (columnHeaders.length > 0) {
    return columnHeaders
      .map((header) => displayValue(viewRow?.[header.id] ?? row?.[header.id]))
      .join(" ")
      .toLowerCase();
  }

  const values: string[] = [];
  const collect = (value: unknown) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }
    if (typeof value === "object") {
      Object.values(value as Record<string, unknown>).forEach(collect);
      return;
    }
    const text = String(value).trim();
    if (text) values.push(text);
  };

  collect(viewRow);
  collect(row);
  return values.join(" ").toLowerCase();
}

function displayValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    const str = String(value).trim();
    return str.length ? str : "—";
  }
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    return value
      .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
      .join(", ");
  }
  return String(value);
}


export function TableRowCardWithSearch({
  heading = "",
  url,
  params = {},
  fetchCondition = true,
  isIncludeSearch,
  searchPlaceholder,
  columnHeaders,
  apiDataKey,
  data,
  viewData,
  setData,
  isActionColumn,
  handleAction,
  filtersOptions = [],
  filters,
  setFilters,
  clickRow,
  isDownloadData = false,
  downloadAccessModule,
  moreActions = [],
  isSelectable = false,
  selectActions = [],
  donotFetch = false,
  isIgnoreSearchParams = false,
  handleSearchActionsClicked = (a: any, b: any, c: any, cb: any) => {},
  defaultFilters = {},
  isActionDisabled,
  isActionsHidden,
  onDownload,
  headerExtraControls,
  searchWrapperClassName,
  onClearAllFilters,
  universalLoadingSkeleton = false,
  clientSearchRowFilter,
  renderRowCard,
  cardContainerClassName,
  pageSizeOptions = [10, 30, 50, 100],
}: Props) {
  const { user, canAccess } = useAuth();
  const canDownload =
    !!isDownloadData &&
    (user?.role?.toLowerCase() === "admin" ||
      (!!downloadAccessModule &&
        isPermissionNavRole(user?.role) &&
        canAccess(downloadAccessModule)));

  const { input, setInput, debouncedInput } = useDebouncedInput("", 500);
  const [selected, setSelected] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState({ sortKey: "", sortType: "", sortKeyMain: "" });
  const [searchParams] = useGetSearchParams();
  const skip = (page - 1) * limit;
  const shouldFetch = fetchCondition && !donotFetch;
  const filtersKey = JSON.stringify(filters ?? {});

  const processedFilters = useMemo(() => {
    const processed = { ...filters };
    if (processed.caretakers && Array.isArray(processed.caretakers) && processed.caretakers.length > 0) {
      processed.caretakers = processed.caretakers.join(",");
    } else if (processed.caretakers && Array.isArray(processed.caretakers) && processed.caretakers.length === 0) {
      delete processed.caretakers;
    }
    if (processed.patientType === "all" || processed.patientType === "") {
      delete processed.patientType;
    }
    return processed;
  }, [filtersKey]);

  const { loading } = useGetData(
    url,
    {
      search: debouncedInput,
      ...params,
      page,
      limit,
      skip,
      ...(isIgnoreSearchParams ? {} : searchParams),
      ...(sort?.sortType !== ""
        ? {
            sortKey: sort.sortKeyMain !== "" ? sort.sortKeyMain : sort.sortKey,
            sortType: sort.sortType,
          }
        : {}),
      ...processedFilters,
    },
    false,
    shouldFetch,
    (err, apidata) => {
      if (!apidata || err) return;
      const items = apiDataKey
        ? (apidata as any)?.data?.[apiDataKey]
        : (apidata as any)?.data;
      setData(items);
      const root = (apidata as any)?.data as Record<string, any> | undefined;
      const rawTotal =
        root?.total ?? root?.pagination?.total ?? root?.pagination?.count;
      const totalRecords = Number(rawTotal);
      const pageCount =
        Number.isFinite(totalRecords) && totalRecords >= 0
          ? Math.max(1, Math.ceil(totalRecords / limit))
          : 1;
      setTotalPages(pageCount);
    },
  );

  const searchQuery = debouncedInput.trim().toLowerCase();

  const filteredPairs = useMemo(() => {
    const cols = (data || []) as any[];
    const viewsRaw = viewData as any[] | undefined;
    const views =
      Array.isArray(viewsRaw) && viewsRaw.length === cols.length
        ? viewsRaw
        : cols;
    const q = searchQuery;
    const shouldClientFilter = donotFetch && isIncludeSearch && q.length > 0;
    if (!shouldClientFilter) {
      return { cols, views };
    }
    const fc: any[] = [];
    const fv: any[] = [];
    cols.forEach((row, i) => {
      const view = views[i] ?? row;
      const haystack = buildRowSearchHaystack(row, view, columnHeaders);
      const matches = clientSearchRowFilter
        ? clientSearchRowFilter(row, q) || haystack.includes(q)
        : haystack.includes(q);
      if (matches) {
        fc.push(row);
        fv.push(view);
      }
    });
    return { cols: fc, views: fv };
  }, [
    data,
    viewData,
    searchQuery,
    isIncludeSearch,
    clientSearchRowFilter,
    columnHeaders,
  ]);

  const maxFilteredPage = Math.max(
    1,
    Math.ceil(filteredPairs.cols.length / limit) || 1,
  );

  const displayCols = useMemo(() => {
    if (!donotFetch) return filteredPairs.cols;
    const safePage = Math.min(page, maxFilteredPage);
    const start = (safePage - 1) * limit;
    return filteredPairs.cols.slice(start, start + limit);
  }, [donotFetch, filteredPairs.cols, page, limit, maxFilteredPage]);

  const displayViews = useMemo(() => {
    if (!donotFetch) return filteredPairs.views;
    const safePage = Math.min(page, maxFilteredPage);
    const start = (safePage - 1) * limit;
    return filteredPairs.views.slice(start, start + limit);
  }, [donotFetch, filteredPairs.views, page, limit, maxFilteredPage]);

  useEffect(() => {
    if (!donotFetch) return;
    setTotalPages(maxFilteredPage);
  }, [donotFetch, maxFilteredPage]);

  useEffect(() => {
    if (!donotFetch) return;
    if (page > maxFilteredPage) setPage(maxFilteredPage);
  }, [donotFetch, maxFilteredPage, page]);

  useEffect(() => {
    if (!donotFetch || !isIncludeSearch) return;
    setPage(1);
  }, [debouncedInput, donotFetch, isIncludeSearch]);

  useEffect(() => {
    if (donotFetch) return;
    setPage(1);
  }, [debouncedInput, donotFetch, filtersKey]);

  const visibleHeaders = columnHeaders;

  const handleDownload = async () => {
    const columnIds = columnHeaders.map((header) => header.id);
    if (onDownload) {
      await onDownload({
        search: debouncedInput,
        visibleColumnIds: columnIds,
        columnOrder: columnIds,
        visibleColumns: Object.fromEntries(columnIds.map((id) => [id, true])),
        sort:
          sort?.sortType !== ""
            ? { sortKey: sort.sortKeyMain !== "" ? sort.sortKeyMain : sort.sortKey, sortType: sort.sortType }
            : null,
      });
      return;
    }
    const exportData = (
      viewData && viewData?.length > 0 ? viewData : data || []
    ).map((row: any) => {
      const exported: Record<string, any> = {};
      columnHeaders.forEach((header) => {
        let value = row[header.id];
        if (Array.isArray(value)) {
          value = value.map((item: any) => item?.name || item).join(", ");
        }
        exported[header.label] = value ?? "";
      });
      return exported;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, heading);
    XLSX.writeFile(
      wb,
      `${heading?.toLowerCase().replace(/\s+/g, "_") || "data"}.xlsx`,
    );
  };

  const displayRows = useMemo(
    () =>
      displayCols.map((row: any, index: number) => ({
        row,
        viewRow: displayViews?.[index] ?? row,
        index,
      })),
    [displayCols, displayViews],
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const isAllSelected = useMemo(() => {
    if (displayRows.length === 0) return false;
    return displayRows.every((r, i) => selectedSet.has(rowIdValue(r.row, i)));
  }, [displayRows, selectedSet]);

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelected([]);
      return;
    }
    const ids = displayRows.map((r, i) => rowIdValue(r.row, i));
    setSelected(ids);
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x: string) => x !== id);
    });
  };

  const showActionsArea = isSelectable && selectActions.length > 0 && selected.length > 0;

  return (
    <Card className="teachers gap-2 rounded-none border-0 bg-transparent p-0 shadow-none">
      <CardHeader className="px-0.5 pb-0 pt-1">
        <div className="flex w-full min-w-0 flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          {isIncludeSearch && (
            <div
              className={clsx(
                "relative w-full md:w-64 md:basis-1/2",
                searchWrapperClassName,
              )}
            >
              <Search className="text-muted-foreground absolute left-3 top-3 h-4 w-4" />
              <Input
                placeholder={searchPlaceholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="ignore pl-9 pr-9"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  className="text-muted-foreground hover:text-foreground absolute right-3 top-3 h-4 w-4 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <div
            className={clsx(
              "mb-4 flex flex-wrap items-center gap-2 md:mb-0",
              isIncludeSearch && "md:ml-auto md:justify-end",
              !isIncludeSearch && "w-full justify-end",
            )}
          >
            {headerExtraControls}
            {filtersOptions.length > 0 && (
              <FilterDropdown
                filters={filters}
                filtersOptions={filtersOptions}
                setFilters={setFilters}
                defaultFilters={defaultFilters}
                onClearAllFilters={onClearAllFilters}
              />
            )}
            {canDownload && (
              <Button onClick={handleDownload}>
                <Download className="mr-2 h-4 w-4" /> Data
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div
          className={clsx(
            "flex gap-4",
            showActionsArea ? "justify-between" : "justify-end",
          )}
        >
          {showActionsArea && (
            <div className="flex h-fit items-center gap-2">
              {selectActions.map((action: any, idx: number) => (
                <React.Fragment key={action?.key ?? idx}>
                  {!action?.isMulti ? (
                    <Select
                      value=""
                      onValueChange={(v) =>
                        handleSearchActionsClicked(selected, action.key, v, () => {
                          setSelected([]);
                        })
                      }
                    >
                      <SelectTrigger className="teachers h-9 w-40 rounded-full border border-[#CFD2EB] bg-[#E8E9F3] px-3 py-1 pr-0 text-sm text-black [>svg]:text-black">
                        <SelectValue placeholder={action.label} />
                      </SelectTrigger>
                      <SelectContent>
                        {action.values.map((v: any) => (
                          <SelectItem key={v.key} value={v.key}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <AssignMultiSelect
                      placeholder={action.label}
                      options={action?.values || []}
                      handleSelectionDone={(v: any) =>
                        handleSearchActionsClicked(selected, action.key, v, () => {
                          setSelected([]);
                        })
                      }
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 bg-transparent pb-2">
          {(() => {
            const effectiveLoading = !donotFetch && loading;

            if (effectiveLoading) {
              return <PatientV2CircularLoader />;
            }

            return (
              <div
                className={clsx(
                  renderRowCard ? patientV2ExpandableCardListClass : "space-y-3 p-3",
                  cardContainerClassName,
                )}
              >
                {displayRows.length === 0 ? (
                  <div className="text-muted-foreground rounded-lg border border-dashed border-border/50 bg-transparent p-4 text-sm">
                    No records found.
                  </div>
                ) : (
                  displayRows.map(({ row, viewRow, index }) => {
                    const id = rowIdValue(row, index);
                    const isSelected = selectedSet.has(id);
                    const primaryActionAllowed = !isActionDisabled?.(row);
                    const rowActions = (moreActions || []).filter((a) => a?.isShow?.(row));

                    const context: RenderRowCardContext = {
                      row,
                      viewRow,
                      index,
                      isSelected,
                      toggleSelected: (checked: boolean) => toggleOne(id, checked),
                      visibleHeaders,
                      actions: rowActions,
                      onPrimaryAction:
                        isActionColumn && primaryActionAllowed && !isActionsHidden?.(row)
                          ? () => handleAction(row)
                          : undefined,
                    };

                    if (renderRowCard) {
                      return (
                        <div
                          key={String(id)}
                          onClick={() => clickRow?.(row)}
                          className="cursor-pointer"
                        >
                          {renderRowCard(context)}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={String(id)}
                        onClick={() => clickRow?.(row)}
                        className="rounded-lg border border-border bg-card p-3 shadow-sm transition-colors hover:bg-card"
                      >
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 md:grid-cols-2">
                            {visibleHeaders.map((h) => (
                              <div key={h.id} className="min-w-0">
                                <p className={patientV2CardFieldLabelClass}>{h.label}</p>
                                <p className={patientV2CardFieldValueClass}>
                                  {displayValue(viewRow?.[h.id])}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {isSelectable ? (
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => toggleOne(id, !!checked)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : null}
                            {isActionColumn && !isActionsHidden?.(row) ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!primaryActionAllowed}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (primaryActionAllowed) handleAction(row);
                                }}
                              >
                                Action
                              </Button>
                            ) : null}
                            {rowActions.map((action, actionIndex) =>
                              action.render ? (
                                <div
                                  key={`${action.label}-${actionIndex}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {action.render(row)}
                                </div>
                              ) : (
                                <Button
                                  key={`${action.label}-${actionIndex}`}
                                  size="sm"
                                  variant="ghost"
                                  disabled={action.isDisabled?.(row)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick?.(row);
                                  }}
                                >
                                  {action.icon}
                                  {action.label ? <span className="ml-1">{action.label}</span> : null}
                                </Button>
                              ),
                            )}
                          </div>
                        </div>
                        <TableRowCardDates
                          row={row as Record<string, unknown>}
                          viewRow={viewRow as Record<string, unknown>}
                          className="mt-2"
                        />
                      </div>
                    );
                  })
                )}
              </div>
            );
          })()}

          <div className="px-3">
            <Pagination
              page={page}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              total={totalPages}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default TableRowCardWithSearch;

